import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Shield, Eye, ChevronDown, ChevronRight, Crown } from 'lucide-react';
import { usersApi } from '../../api/endpoints/users';
import { useStore } from '../../contexts/StoreContext';
import { TransferOwnershipModal } from '../../components/stores/TransferOwnershipModal';

// Store ownership transfer is built end-to-end but hidden from the UI for now.
// Flip to true to re-expose the owner-only transfer button + modal (the backend
// endpoints remain live regardless).
const STORE_OWNERSHIP_TRANSFER_ENABLED = false;
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { DateFilterBar } from '../../components/ui/DateFilterBar';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Alert';
import { USER_ROLES } from '../../lib/constants';
import { ApiError } from '../../types/api';
import { usernameFromEmail } from '../../lib/username';
import type { StoreTeamUser } from '../../types/user';

// --- Permission grid types & helpers ---

const PERMISSION_SECTIONS = [
  'info', 'customers', 'products', 'orders', 'coupons', 'reviews', 'pages', 'paylinks',
  'shipping', 'analyticalLinks', 'wallet', 'transactions', 'export', 'users', 'settings',
] as const;

const SUB_PERMISSIONS = ['view', 'add', 'edit', 'delete'] as const;
type SubPermission = (typeof SUB_PERMISSIONS)[number];

/**
 * Which actions each section actually enforces on the backend. Sections omitted
 * here default to the full view/add/edit/delete set. Only the listed actions
 * are shown in the grid so merchants don't tick checkboxes that map to nothing.
 *
 * Note: the backend treats the grid's "add" as a synonym for "create" — coupons
 * and analyticalLinks check `.create`, products/orders/etc. check `.add` — so we
 * keep the single "add" column here and let the backend reconcile the spelling.
 */
const SECTION_ACTIONS: Record<string, readonly SubPermission[]> = {
  info: ['edit'],
  shipping: ['view', 'edit'],
  reviews: ['view', 'edit', 'delete'],
  coupons: ['view', 'add', 'edit', 'delete'],
  analyticalLinks: ['view', 'add', 'delete'],
};

const sectionActions = (section: string): readonly SubPermission[] =>
  SECTION_ACTIONS[section] ?? SUB_PERMISSIONS;

type SectionPerms = { all: boolean; view: boolean; add: boolean; edit: boolean; delete: boolean };
type PermissionGrid = Record<string, SectionPerms>;

function createEmptyGrid(): PermissionGrid {
  const grid: PermissionGrid = {};
  for (const section of PERMISSION_SECTIONS) {
    grid[section] = { all: false, view: false, add: false, edit: false, delete: false };
  }
  return grid;
}

/** Convert a flat permissions array (e.g. ["customers.view","orders"]) to a PermissionGrid */
function permissionsToGrid(perms: string[]): PermissionGrid {
  const grid = createEmptyGrid();
  for (const perm of perms) {
    // True full access: the backend grants everything (incl. categories not in
    // this grid, e.g. info/coupons/reviews/shipping) for "*"/"all".
    if (perm === '*' || perm === 'all') {
      for (const section of PERMISSION_SECTIONS) {
        grid[section] = { all: true, view: true, add: true, edit: true, delete: true };
      }
      continue;
    }
    const parts = perm.split('.');
    const section = parts[0];
    const action = parts[1];
    if (!section || !grid[section]) continue;
    if (!action) {
      // Bare section = full access to it -> tick every action it supports.
      const g: SectionPerms = { all: true, view: false, add: false, edit: false, delete: false };
      for (const a of sectionActions(section)) g[a] = true;
      grid[section] = g;
    } else if (SUB_PERMISSIONS.includes(action as SubPermission)) {
      grid[section][action as keyof SectionPerms] = true;
    }
  }
  // sync "all" flags against each section's supported actions
  for (const section of PERMISSION_SECTIONS) {
    const s = grid[section];
    if (s) s.all = sectionActions(section).every((a) => s[a]);
  }
  return grid;
}

/** Convert a PermissionGrid back to a flat permissions array */
function gridToPermissions(grid: PermissionGrid): string[] {
  // Every section fully granted => emit a real wildcard so the role also covers
  // backend permission categories that aren't in this grid (info.edit for store
  // logo/name, coupons, reviews, shipping, analyticalLinks, ...). Listing the
  // individual sections would silently leave those denied.
  if (PERMISSION_SECTIONS.every((s) => grid[s]?.all)) {
    return ['*'];
  }
  const result: string[] = [];
  for (const section of PERMISSION_SECTIONS) {
    const s = grid[section];
    if (!s) continue;
    if (s.all) {
      result.push(section); // shorthand: full access
    } else {
      for (const action of sectionActions(section)) {
        if (s[action]) result.push(`${section}.${action}`);
      }
    }
  }
  return result;
}

/**
 * Starting permission sets per role, applied when a role is picked in the
 * add/edit forms so merchants don't begin from an empty grid. These are just
 * sensible defaults; the grid stays fully editable afterwards. 'owner' is not
 * listed (owners always have full access and can't be assigned from this UI),
 * and 'new' (custom role) intentionally starts empty so the merchant builds it.
 *
 * Format matches gridToPermissions(): '*' = everything, a bare section name =
 * full access to that section, 'section.action' = a single action.
 */
const ROLE_PRESETS: Record<string, string[]> = {
  // Runs the entire store, including team, settings, and wallet.
  admin: ['*'],
  // Runs day-to-day operations (catalog, orders, customers, marketing) but not
  // the team, store settings, or money withdrawal — those stay owner/admin.
  manager: [
    'info.edit',
    'customers', 'products', 'orders', 'coupons', 'reviews', 'pages', 'paylinks',
    'shipping', 'analyticalLinks',
    'transactions.view', 'export.view', 'wallet.view',
  ],
  // Handles orders and customers, creates paylinks, sees the catalog. No
  // deletes, and no settings / team / money.
  staff: [
    'customers.view', 'customers.add', 'customers.edit',
    'products.view', 'products.edit',
    'orders.view', 'orders.add', 'orders.edit',
    'coupons.view',
    'reviews.view', 'reviews.edit',
    'pages.view',
    'paylinks.view', 'paylinks.add',
    'shipping.view',
    'analyticalLinks.view',
  ],
  // Read-only across the main business sections.
  viewer: [
    'customers.view', 'products.view', 'orders.view', 'coupons.view',
    'reviews.view', 'pages.view', 'paylinks.view', 'shipping.view',
    'analyticalLinks.view', 'transactions.view', 'wallet.view',
  ],
};

// --- Reusable PermissionsGrid component ---

function PermissionsGridEditor({
  grid,
  onChange,
  t,
}: {
  grid: PermissionGrid;
  onChange: (grid: PermissionGrid) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}) {
  const sectionLabels: Record<string, string> = {
    info: t('permissions.storeInfo', 'Store info'),
    customers: t('sidebar.customers'),
    products: t('sidebar.products'),
    orders: t('sidebar.orders'),
    coupons: t('sidebar.coupons'),
    reviews: t('sidebar.reviews'),
    pages: t('sidebar.pages'),
    paylinks: t('sidebar.paylinks'),
    shipping: t('sidebar.shipping'),
    analyticalLinks: t('sidebar.analyticalLinks'),
    wallet: t('sidebar.wallet'),
    transactions: t('permissions.transactions', 'Transactions'),
    export: t('permissions.export', 'Export'),
    users: t('sidebar.team'),
    settings: t('sidebar.settings'),
  };

  const handleToggle = (section: string, action: 'all' | SubPermission) => {
    const current = grid[section] || { all: false, view: false, add: false, edit: false, delete: false };
    const next = { ...grid, [section]: { ...current } };
    const actions = sectionActions(section);
    if (action === 'all') {
      const newVal = !current.all;
      const g: SectionPerms = { all: newVal, view: false, add: false, edit: false, delete: false };
      for (const a of actions) g[a] = newVal;
      next[section] = g;
    } else {
      const sec = next[section]!;
      sec[action] = !sec[action];
      sec.all = actions.every((a) => sec[a]);
    }
    onChange(next as PermissionGrid);
  };

  const allGranted = PERMISSION_SECTIONS.every((s) => grid[s]?.all);

  const handleToggleAll = () => {
    const newVal = !allGranted;
    const next: PermissionGrid = {};
    for (const section of PERMISSION_SECTIONS) {
      const g: SectionPerms = { all: newVal, view: false, add: false, edit: false, delete: false };
      for (const a of sectionActions(section)) g[a] = newVal;
      next[section] = g;
    }
    onChange(next);
  };

  return (
    <div className="overflow-x-auto">
      <div className="mb-3 flex items-center justify-between rounded-lg border border-accent/30 bg-accent/5 px-3 py-2">
        <div>
          <p className="text-sm font-medium text-text-primary">{t('users.allowAll', 'Allow all')}</p>
          <p className="text-xs text-text-muted">{t('users.allowAllHint', 'Grant full access to every section')}</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={allGranted}
            onChange={handleToggleAll}
            className="h-4 w-4 rounded border-border-default text-accent accent-accent cursor-pointer"
          />
          <span className="text-xs font-medium text-accent">{allGranted ? t('common.on', 'On') : t('common.off', 'Off')}</span>
        </label>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border-default text-text-muted">
            <th className="py-2 pe-3 text-start font-medium">{t('users.section', 'Section')}</th>
            <th className="px-2 py-2 text-center font-medium">{t('common.all')}</th>
            <th className="px-2 py-2 text-center font-medium">{t('common.view')}</th>
            <th className="px-2 py-2 text-center font-medium">{t('common.add')}</th>
            <th className="px-2 py-2 text-center font-medium">{t('common.edit')}</th>
            <th className="px-2 py-2 text-center font-medium">{t('common.delete')}</th>
          </tr>
        </thead>
        <tbody>
          {PERMISSION_SECTIONS.map((section) => (
            <tr key={section} className="border-b border-border-default/50 hover:bg-bg-hover/50 transition-colors">
              <td className="py-2 pe-3 font-medium text-text-secondary">{sectionLabels[section]}</td>
              {(['all', ...SUB_PERMISSIONS] as const).map((action) => {
                const supported =
                  action === 'all' || sectionActions(section).includes(action as SubPermission);
                return (
                  <td key={action} className="px-2 py-2 text-center">
                    {supported ? (
                      <input
                        type="checkbox"
                        checked={grid[section]?.[action] ?? false}
                        onChange={() => handleToggle(section, action)}
                        className="h-3.5 w-3.5 rounded border-border-default text-accent accent-accent cursor-pointer"
                      />
                    ) : (
                      <span className="text-text-muted/30 select-none">·</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Main component ---

export function UserList() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Sort & date filters
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Modal visibility
  const [showAddModal, setShowAddModal] = useState(false);
  const [editRoleTarget, setEditRoleTarget] = useState<StoreTeamUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StoreTeamUser | null>(null);
  const [viewPermissionsTarget, setViewPermissionsTarget] = useState<StoreTeamUser | null>(null);

  // Add user form state
  const { role: currentRole, currentStore } = useStore();
  const [showTransfer, setShowTransfer] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('staff');
  const [customRoleName, setCustomRoleName] = useState('');
  const [addPermissions, setAddPermissions] = useState<PermissionGrid>(() => permissionsToGrid(ROLE_PRESETS.staff));
  const [showPermGrid, setShowPermGrid] = useState(false);
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');

  // Edit role form state
  const [editSelectedRole, setEditSelectedRole] = useState('staff');
  const [editCustomRoleName, setEditCustomRoleName] = useState('');
  const [editPermissions, setEditPermissions] = useState<PermissionGrid>(createEmptyGrid);
  const [showEditPermGrid, setShowEditPermGrid] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['users', storeId, { sort: sortDir, startDate, endDate }],
    queryFn: () => usersApi.list(storeId!, {
      sort: sortDir !== 'desc' ? sortDir : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    enabled: !!storeId,
  });

  const resolveRoleName = (role: string, custom: string) => {
    return role === 'new' ? custom.trim() : role;
  };

  const resetAddForm = () => {
    setAddEmail('');
    setSelectedRole('staff');
    setCustomRoleName('');
    setAddPermissions(permissionsToGrid(ROLE_PRESETS.staff));
    setShowPermGrid(false);
    setIsNewAccount(false);
    setNewUsername('');
    setUsernameError('');
  };

  // Picking a role pre-fills the permission grid with that role's defaults and
  // reveals the grid so the merchant can see/adjust what was applied. 'new'
  // (custom role) is left untouched so they build it from scratch.
  const applyRolePreset = (
    role: string,
    setGrid: (g: PermissionGrid) => void,
    revealGrid: () => void,
  ) => {
    if (role === 'new') return;
    const preset = ROLE_PRESETS[role];
    if (!preset) return;
    setGrid(permissionsToGrid(preset));
    revealGrid();
  };

  const addMutation = useMutation({
    mutationFn: () => {
      const perms = gridToPermissions(addPermissions);
      return usersApi.add(storeId!, {
        email: addEmail,
        newRoleName: resolveRoleName(selectedRole, customRoleName),
        permissions: perms.length > 0 ? perms : undefined,
        ...(isNewAccount ? { createAccount: { username: newUsername } } : {}),
      });
    },
    // The backend replies 200 for every outcome and signals the result via the
    // `status` field (mirrors the legacy /stores/users/add contract), so we must
    // branch on it here - a resolved request does NOT mean the user was added.
    onSuccess: (data) => {
      const status = data?.status;

      if (status === 'ok') {
        queryClient.invalidateQueries({ queryKey: ['users', storeId] });
        toast('success', t('users.addSuccess'));
        setShowAddModal(false);
        resetAddForm();
        return;
      }

      if (status === 'user not found') {
        // No account for this email yet -> reveal the username field so the
        // merchant can create one (createAccount flow). 200, not 404, so this
        // never reaches onError. Prefill the username from the email's local
        // part (xyasserksa@gmail.com -> "xyasserksa") so the common case is a
        // single extra click; the merchant can still edit it.
        if (!isNewAccount) {
          setIsNewAccount(true);
          setNewUsername(usernameFromEmail(addEmail));
          setUsernameError('');
        }
        return;
      }

      if (status === 'username taken') {
        // The prefilled (or chosen) username is taken -> ask for a different one.
        setUsernameError(t('users.usernameTaken', 'This username is taken, please choose another.'));
        return;
      }

      if (status === 'user already exists') {
        toast('error', t('users.alreadyExists', 'This user is already a team member.'));
        return;
      }

      toast('error', t('common.error'));
    },
    onError: (error) => {
      // Defensive: in case the backend ever switches to a real 404 for missing
      // accounts, keep honoring the create-account fallback here too.
      const isUserNotFound =
        (error instanceof ApiError &&
          (error.code === 'USER_NOT_FOUND' ||
            error.status === 404 ||
            /user.*not\s*found/i.test(error.message))) ||
        (error instanceof Error && /user.*not\s*found/i.test(error.message));

      if (isUserNotFound && !isNewAccount) {
        setIsNewAccount(true);
        setNewUsername('');
      } else {
        toast('error', t('common.error'));
      }
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, roleName, permissions }: { userId: string; roleName: string; permissions?: string[] }) =>
      usersApi.updateRole(storeId!, userId, { roleName, permissions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', storeId] });
      toast('success', t('common.success'));
      setEditRoleTarget(null);
      setShowEditPermGrid(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => usersApi.delete(storeId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', storeId] });
      toast('success', t('common.success'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const roleColor = (role: string) => {
    if (role === 'owner') return 'accent';
    if (role === 'admin') return 'info';
    if (role === 'manager') return 'warning';
    return 'default';
  };

  const openEditRole = (user: StoreTeamUser) => {
    const isKnownRole = (USER_ROLES as readonly string[]).includes(user.role);
    setEditRoleTarget(user);
    setEditSelectedRole(isKnownRole ? user.role : 'new');
    setEditCustomRoleName(isKnownRole ? '' : user.role);
    setEditPermissions(permissionsToGrid(user.permissions ?? []));
    setShowEditPermGrid(true);
  };

  // Role options: standard roles (excluding owner) + "Create new role"
  const roleOptions = [
    ...USER_ROLES.filter((r) => r !== 'owner').map((r) => ({ value: r, label: t(`roles.${r}`, r) })),
    { value: 'new', label: t('users.createNewRole', 'Create new role') },
  ];

  const users = data?.users ?? [];

  const handleAddEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddEmail(e.target.value);
    if (isNewAccount) {
      setIsNewAccount(false);
      setNewUsername('');
      setUsernameError('');
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetAddForm();
  };

  const isAddDisabled =
    !addEmail.trim() ||
    (selectedRole === 'new' && !customRoleName.trim()) ||
    (isNewAccount && !newUsername.trim());
  const isEditSaveDisabled = editSelectedRole === 'new' && !editCustomRoleName.trim();

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('sidebar.team')}</h1>
        <div className="flex items-center gap-2">
          {STORE_OWNERSHIP_TRANSFER_ENABLED && currentRole === 'owner' && (
            <Button variant="secondary" onClick={() => setShowTransfer(true)}>
              <Crown className="h-4 w-4" />
              {t('users.transferOwnership', 'نقل الملكية')}
            </Button>
          )}
          <PermissionGate requires="users.edit">
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4" />
              {t('users.addUser')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      {STORE_OWNERSHIP_TRANSFER_ENABLED && currentRole === 'owner' && storeId && (
        <TransferOwnershipModal
          storeId={storeId}
          storeName={currentStore?.name}
          isOpen={showTransfer}
          onClose={() => setShowTransfer(false)}
        />
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          options={[
            { value: 'desc', label: t('common.newestFirst', 'Newest first') },
            { value: 'asc', label: t('common.oldestFirst', 'Oldest first') },
          ]}
          value={sortDir}
          onChange={(e) => setSortDir(e.target.value as 'desc' | 'asc')}
          className="w-36"
        />
      </div>
      <DateFilterBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        className="mb-4"
      />

      {users.length === 0 ? (
        <EmptyState title={t('common.noData')} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {users.map((user) => (
            <div key={user._id} className="rounded-xl border border-border-default bg-bg-secondary p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent font-bold text-sm">
                    {(user.username || user.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium">{user.username || user.email}</p>
                    {user.email && <p className="text-xs text-text-muted">{user.email}</p>}
                  </div>
                </div>
                <Badge variant={roleColor(user.role) as 'accent' | 'info' | 'warning' | 'default'}>
                  {t(`roles.${user.role}`, user.role)}
                </Badge>
              </div>
              {user.accepted === false && (
                <Badge variant="warning" className="mb-3">{t('users.pendingAcceptance')}</Badge>
              )}
              <PermissionGate requires="users.edit">
                <div className="flex items-center gap-1 border-t border-border-default pt-3 mt-3">
                  <button
                    onClick={() => setViewPermissionsTarget(user)}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                    title={t('users.viewPermissions', 'View permissions')}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {user.role !== 'owner' && (
                    <>
                      <button
                        onClick={() => openEditRole(user)}
                        className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                        title={t('users.editRole')}
                      >
                        <Shield className="h-4 w-4" />
                      </button>
                      <div className="flex-1" />
                      <button
                        onClick={() => setDeleteTarget(user)}
                        className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </PermissionGate>
            </div>
          ))}
        </div>
      )}

      {/* Add user modal */}
      <Modal
        isOpen={showAddModal}
        onClose={handleCloseAddModal}
        title={isNewAccount ? t('users.createAccount', 'Create Account') : t('users.addUser')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseAddModal}>{t('common.cancel')}</Button>
            <Button onClick={() => addMutation.mutate()} isLoading={addMutation.isPending} disabled={isAddDisabled}>
              {isNewAccount ? t('users.createAndAdd', 'Create & Add User') : t('common.add')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label={t('common.email')} type="email" required value={addEmail} onChange={handleAddEmailChange} />

          {isNewAccount && (
            <div className="space-y-3">
              <div className="rounded-lg border border-accent/30 bg-accent/5 px-4 py-3">
                <p className="text-sm text-text-secondary">
                  {t('users.noAccountFound', 'No account found with this email. Fill in a username to create a new account and add them to your team.')}
                </p>
              </div>
              <Input
                label={t('users.username', 'Username')}
                required
                value={newUsername}
                onChange={(e) => {
                  setNewUsername(e.target.value);
                  if (usernameError) setUsernameError('');
                }}
                error={usernameError || undefined}
                placeholder={t('users.usernamePlaceholder', 'Enter a username')}
              />
            </div>
          )}
          <Select
            label={t('stores.role')}
            options={roleOptions}
            value={selectedRole}
            onChange={(e) => {
              const r = e.target.value;
              setSelectedRole(r);
              applyRolePreset(r, setAddPermissions, () => setShowPermGrid(true));
            }}
          />
          {selectedRole !== 'new' && (
            <p className="-mt-2 text-xs text-text-muted">
              {t('users.rolePresetHint', 'Default permissions for this role are applied below. Open "Custom Permissions" to fine-tune them.')}
            </p>
          )}
          {selectedRole === 'new' && (
            <Input
              label={t('users.newRoleName', 'Role name')}
              required
              value={customRoleName}
              onChange={(e) => setCustomRoleName(e.target.value)}
              placeholder={t('users.newRoleNamePlaceholder', 'e.g. Accountant')}
            />
          )}

          {/* Permissions grid */}
          <div className="border-t border-border-default pt-3">
            <button
              type="button"
              onClick={() => setShowPermGrid(!showPermGrid)}
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-accent transition-colors"
            >
              {showPermGrid ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {t('users.customPermissions', 'Custom Permissions')}
            </button>
            {showPermGrid && (
              <div className="mt-3 rounded-lg border border-border-default bg-bg-primary p-3">
                <PermissionsGridEditor grid={addPermissions} onChange={setAddPermissions} t={t} />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Edit role modal */}
      <Modal
        isOpen={!!editRoleTarget}
        onClose={() => setEditRoleTarget(null)}
        title={t('users.editRole')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditRoleTarget(null)}>{t('common.cancel')}</Button>
            <Button
              onClick={() => {
                if (!editRoleTarget) return;
                const perms = gridToPermissions(editPermissions);
                updateRoleMutation.mutate({
                  userId: editRoleTarget.userId,
                  roleName: resolveRoleName(editSelectedRole, editCustomRoleName),
                  permissions: perms.length > 0 ? perms : undefined,
                });
              }}
              isLoading={updateRoleMutation.isPending}
              disabled={isEditSaveDisabled}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label={t('stores.role')}
            options={roleOptions}
            value={editSelectedRole}
            onChange={(e) => {
              const r = e.target.value;
              setEditSelectedRole(r);
              applyRolePreset(r, setEditPermissions, () => setShowEditPermGrid(true));
            }}
          />
          {editSelectedRole !== 'new' && (
            <p className="-mt-2 text-xs text-text-muted">
              {t('users.rolePresetHintEdit', 'Switching the role replaces the permissions below with that role\'s defaults. Open "Custom Permissions" to fine-tune them.')}
            </p>
          )}
          {editSelectedRole === 'new' && (
            <Input
              label={t('users.newRoleName', 'Role name')}
              required
              value={editCustomRoleName}
              onChange={(e) => setEditCustomRoleName(e.target.value)}
              placeholder={t('users.newRoleNamePlaceholder', 'e.g. Accountant')}
            />
          )}

          {/* Permissions grid */}
          <div className="border-t border-border-default pt-3">
            <button
              type="button"
              onClick={() => setShowEditPermGrid(!showEditPermGrid)}
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-accent transition-colors"
            >
              {showEditPermGrid ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {t('users.customPermissions', 'Custom Permissions')}
            </button>
            {showEditPermGrid && (
              <div className="mt-3 rounded-lg border border-border-default bg-bg-primary p-3">
                <PermissionsGridEditor grid={editPermissions} onChange={setEditPermissions} t={t} />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* View permissions modal */}
      <Modal
        isOpen={!!viewPermissionsTarget}
        onClose={() => setViewPermissionsTarget(null)}
        title={`${viewPermissionsTarget?.username || viewPermissionsTarget?.email || ''} - ${t('users.permissions', 'Permissions')}`}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant={roleColor(viewPermissionsTarget?.role || '') as 'accent' | 'info' | 'warning' | 'default'}>
              {t(`roles.${viewPermissionsTarget?.role}`, viewPermissionsTarget?.role || '')}
            </Badge>
          </div>
          {viewPermissionsTarget?.role === 'owner' ? (
            <p className="text-sm text-text-secondary">{t('users.ownerFullAccess', 'Store owner has full access to all features.')}</p>
          ) : (viewPermissionsTarget?.permissions ?? []).length === 0 ? (
            <p className="text-sm text-text-muted">{t('users.noPermissions', 'No specific permissions assigned.')}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(viewPermissionsTarget?.permissions ?? []).map((perm) => (
                <span
                  key={perm}
                  className="inline-flex rounded-md bg-bg-tertiary border border-border-default px-2.5 py-1 text-xs font-mono text-text-secondary"
                >
                  {perm}
                </span>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" isLoading={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.userId)}>
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('users.deleteConfirm')}</p>
        <p className="mt-2 font-medium">{deleteTarget?.username || deleteTarget?.email}</p>
      </Modal>
    </div>
  );
}
